'use client';

import { useEffect, useRef } from 'react';
import { useReducedMotion } from 'motion/react';
import { Mesh, Program, Renderer, Triangle } from 'ogl';

/**
 * Küre — canlı bir ışık kütlesi.
 *
 * Şader React Bits'in "Orb" bileşeninden (MIT,
 * https://reactbits.dev/backgrounds/orb). GLSL'in matematiğine dokunulmadı;
 * kabuk `gradient-waves.tsx` ile aynı gerekçelerle yeniden yazıldı —
 * DESIGN.md §1 ve §2'nin istediklerini kaynak sürüm karşılamıyor:
 *
 *   • **Azaltılmış hareket.** Kaynakta koşulsuz `requestAnimationFrame`
 *     döngüsü var. MotionConfig sonsuz döngüleri kapatmıyor (DESIGN.md §1),
 *     o yüzden burada elle: `prefers-reduced-motion` açıksa tek kare basılıp
 *     duruluyor. Küre kaybolmuyor — aynı görüntünün donmuş hâli kalıyor.
 *   • **Kurulum bir kez.** Kaynakta effect'in bağımlılık dizisi bütün
 *     prop'ları sayıyor: rengi değiştirmek WebGL bağlamını komple yeniden
 *     kuruyor. Burada kurulum boş dizide, prop'lar ikinci effect'ten
 *     uniform'a yazılıyor — tek kare bile kaybedilmiyor.
 *   • **Dokunmatik.** Hover tepkisi yalnızca `(hover: hover)` ve
 *     `(pointer: fine)` cihazlarda açık. Kaynak dinleyiciyi kabın kendisine
 *     bağlıyor; bizde katman `pointer-events: none` (metnin seçimini
 *     yutmaması için), o yüzden dinleyici `window`'a taşındı ve konum
 *     canvas'ın dikdörtgenine göre normalleniyor.
 *   • **Duraklatma.** Sekme arkadayken ya da bölüm görüş alanı dışındayken
 *     döngü duruyor. Kaynakta ikisi de yok, küre sayfa boyunca GPU yakıyor.
 *   • **Piksel bütçesi.** Kaynak `dpr`yi tarayıcıdan olduğu gibi alıyor.
 *     Burada tampon alanı tavanla sınırlanıyor (bkz. MAX_BUFFER_PIXELS).
 *   • **`premultipliedAlpha`.** Kaynak `false` diyor ama şader
 *     `gl_FragColor = vec4(col.rgb * col.a, col.a)` ile zaten çarpılmış renk
 *     üretiyor; tarayıcı ikinci kez çarpınca kenarlar olduğundan koyu
 *     çıkıyor. `true` doğrusu — `gradient-waves.tsx` de öyle.
 *
 * ── Renkler neden uniform ─────────────────────────────────────────────────
 *
 * Kaynakta üç taban rengi GLSL'de `const`: mor, açık mavi, lacivert. Tek ayar
 * `hue` — bütün paleti birlikte döndürüyor. Markanın yeşiline (#12e193)
 * denk gelen dönüş açısı (~120°) ikinci rengi limon sarısına
 * (#adbf00) çeviriyor; palettede o renk yok ve küre ucuz duruyor. Üçü
 * uniform'a çıkarıldı, `hue` yerinde duruyor (varsayılan 0 = renkler olduğu
 * gibi). Şaderin matematiği aynı, değişen yalnızca sabitlerin nereden
 * geldiği.
 *
 * Varsayılanlar tesadüf değil: `color3` hero kabının rampasının alt ucu
 * (#06292f), yani kürenin karanlık gövdesi zemine karışıyor ve kesme çizgisi
 * oluşmuyor. `color1` marka yeşili — başlığın ikinci satırıyla aynı mürekkep.
 *
 * Katman `pointer-events: none` ve `aria-hidden`: dekoratif, içeriğin altında.
 */

export type OrbProps = {
  className?: string;
  /** Ana renk — marka yeşili. Halka bunun etrafında dolanıyor. */
  color1?: string;
  /** İkinci renk. Halka açı boyunca color1 ile bunun arasında geçiyor. */
  color2?: string;
  /** Gövdenin karanlık dolgusu. Zeminle aynı olmalı, yoksa daire çiziliyor. */
  color3?: string;
  /** Üç rengin hepsini birlikte döndürür (derece). 0 = olduğu gibi. */
  hue?: number;
  /** İmleç yaklaşınca yüzeyin dalgalanma miktarı. */
  hoverIntensity?: number;
  /** İmleç yaklaşınca küre kendi ekseninde dönsün mü. */
  rotateOnHover?: boolean;
  /** Hover durumunu kalıcı açar — imleçsiz cihazlarda önizleme için. */
  forceHoverState?: boolean;
};

/**
 * Çizim tamponu tavanı. Bu şader piksel başına tek geçişli (ray-march yok),
 * yani `gradient-waves`ten epey ucuz; tavan yine de var çünkü 2K ekranda
 * 2x dpr ile katman tek başına 4 Mpx'e çıkabiliyor ve o pikseller görünür
 * bir kazanç getirmiyor — küre yumuşak bir ışık kütlesi, kenarı keskin değil.
 */
const MAX_BUFFER_PIXELS = 2_200_000;

/**
 * Azaltılmış hareket modunda basılan tek karenin zamanı.
 *
 * 0 seçilmiyor: `iTime = 0`da gürültü alanı ilk örneğinde ve halkanın
 * parlak noktası tam sağda, saat 3 yönünde duruyor — donmuş kare "hazır
 * değil" gibi okunuyor. 3.4 sn'de kütle yerine oturmuş, parlak nokta
 * yukarı kaymış oluyor.
 */
const STILL_TIME = 3.4;

const hexToRgb = (hex: string): [number, number, number] => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m || !m[1] || !m[2] || !m[3]) return [1, 1, 1];
  return [parseInt(m[1], 16) / 255, parseInt(m[2], 16) / 255, parseInt(m[3], 16) / 255];
};

/* Şader GLSL ES 1.00 (kaynaktaki hâli). ogl varsayılan olarak WebGL2 bağlamı
   açıyor; WebGL2 bu sürümü de derliyor, `#version` satırı olmadığı sürece. */
const vertex = /* glsl */ `
precision highp float;
attribute vec2 position;
attribute vec2 uv;
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position, 0.0, 1.0);
}
`;

const fragment = /* glsl */ `
precision highp float;

uniform float iTime;
uniform vec3 iResolution;
uniform float hue;
uniform float hover;
uniform float rot;
uniform float hoverIntensity;
uniform vec3 backgroundColor;
uniform vec3 uColor1;
uniform vec3 uColor2;
uniform vec3 uColor3;
varying vec2 vUv;

vec3 rgb2yiq(vec3 c) {
  float y = dot(c, vec3(0.299, 0.587, 0.114));
  float i = dot(c, vec3(0.596, -0.274, -0.322));
  float q = dot(c, vec3(0.211, -0.523, 0.312));
  return vec3(y, i, q);
}

vec3 yiq2rgb(vec3 c) {
  float r = c.x + 0.956 * c.y + 0.621 * c.z;
  float g = c.x - 0.272 * c.y - 0.647 * c.z;
  float b = c.x - 1.106 * c.y + 1.703 * c.z;
  return vec3(r, g, b);
}

vec3 adjustHue(vec3 color, float hueDeg) {
  float hueRad = hueDeg * 3.14159265 / 180.0;
  vec3 yiq = rgb2yiq(color);
  float cosA = cos(hueRad);
  float sinA = sin(hueRad);
  float i = yiq.y * cosA - yiq.z * sinA;
  float q = yiq.y * sinA + yiq.z * cosA;
  yiq.y = i;
  yiq.z = q;
  return yiq2rgb(yiq);
}

vec3 hash33(vec3 p3) {
  p3 = fract(p3 * vec3(0.1031, 0.11369, 0.13787));
  p3 += dot(p3, p3.yxz + 19.19);
  return -1.0 + 2.0 * fract(vec3(
    p3.x + p3.y,
    p3.x + p3.z,
    p3.y + p3.z
  ) * p3.zyx);
}

float snoise3(vec3 p) {
  const float K1 = 0.333333333;
  const float K2 = 0.166666667;
  vec3 i = floor(p + (p.x + p.y + p.z) * K1);
  vec3 d0 = p - (i - (i.x + i.y + i.z) * K2);
  vec3 e = step(vec3(0.0), d0 - d0.yzx);
  vec3 i1 = e * (1.0 - e.zxy);
  vec3 i2 = 1.0 - e.zxy * (1.0 - e);
  vec3 d1 = d0 - (i1 - K2);
  vec3 d2 = d0 - (i2 - K1);
  vec3 d3 = d0 - 0.5;
  vec4 h = max(0.6 - vec4(
    dot(d0, d0),
    dot(d1, d1),
    dot(d2, d2),
    dot(d3, d3)
  ), 0.0);
  vec4 n = h * h * h * h * vec4(
    dot(d0, hash33(i)),
    dot(d1, hash33(i + i1)),
    dot(d2, hash33(i + i2)),
    dot(d3, hash33(i + 1.0))
  );
  return dot(vec4(31.316), n);
}

vec4 extractAlpha(vec3 colorIn) {
  float a = max(max(colorIn.r, colorIn.g), colorIn.b);
  return vec4(colorIn.rgb / (a + 1e-5), a);
}

const float innerRadius = 0.6;
const float noiseScale = 0.65;

float light1(float intensity, float attenuation, float dist) {
  return intensity / (1.0 + dist * attenuation);
}

float light2(float intensity, float attenuation, float dist) {
  return intensity / (1.0 + dist * dist * attenuation);
}

vec4 draw(vec2 uv) {
  vec3 color1 = adjustHue(uColor1, hue);
  vec3 color2 = adjustHue(uColor2, hue);
  vec3 color3 = adjustHue(uColor3, hue);

  float ang = atan(uv.y, uv.x);
  float len = length(uv);
  float invLen = len > 0.0 ? 1.0 / len : 0.0;

  float bgLuminance = dot(backgroundColor, vec3(0.299, 0.587, 0.114));

  float n0 = snoise3(vec3(uv * noiseScale, iTime * 0.5)) * 0.5 + 0.5;
  float r0 = mix(mix(innerRadius, 1.0, 0.4), mix(innerRadius, 1.0, 0.6), n0);
  float d0 = distance(uv, (r0 * invLen) * uv);
  float v0 = light1(1.0, 10.0, d0);

  v0 *= smoothstep(r0 * 1.05, r0, len);
  float innerFade = smoothstep(r0 * 0.8, r0 * 0.95, len);
  v0 *= mix(innerFade, 1.0, bgLuminance * 0.7);
  float cl = cos(ang + iTime * 2.0) * 0.5 + 0.5;

  float a = iTime * -1.0;
  vec2 pos = vec2(cos(a), sin(a)) * r0;
  float d = distance(uv, pos);
  float v1 = light2(1.5, 5.0, d);
  v1 *= light1(1.0, 50.0, d0);

  float v2 = smoothstep(1.0, mix(innerRadius, 1.0, n0 * 0.5), len);
  float v3 = smoothstep(innerRadius, mix(innerRadius, 1.0, 0.5), len);

  vec3 colBase = mix(color1, color2, cl);
  float fadeAmount = mix(1.0, 0.1, bgLuminance);

  vec3 darkCol = mix(color3, colBase, v0);
  darkCol = (darkCol + v1) * v2 * v3;
  darkCol = clamp(darkCol, 0.0, 1.0);

  vec3 lightCol = (colBase + v1) * mix(1.0, v2 * v3, fadeAmount);
  lightCol = mix(backgroundColor, lightCol, v0);
  lightCol = clamp(lightCol, 0.0, 1.0);

  vec3 finalCol = mix(darkCol, lightCol, bgLuminance);

  return extractAlpha(finalCol);
}

vec4 mainImage(vec2 fragCoord) {
  vec2 center = iResolution.xy * 0.5;
  float size = min(iResolution.x, iResolution.y);
  vec2 uv = (fragCoord - center) / size * 2.0;

  float angle = rot;
  float s = sin(angle);
  float c = cos(angle);
  uv = vec2(c * uv.x - s * uv.y, s * uv.x + c * uv.y);

  uv.x += hover * hoverIntensity * 0.1 * sin(uv.y * 10.0 + iTime);
  uv.y += hover * hoverIntensity * 0.1 * sin(uv.x * 10.0 + iTime);

  return draw(uv);
}

void main() {
  vec2 fragCoord = vUv * iResolution.xy;
  vec4 col = mainImage(fragCoord);
  gl_FragColor = vec4(col.rgb * col.a, col.a);
}
`;

/* Adları tek tek sayılıyor: tsconfig'de `noUncheckedIndexedAccess` açık,
   indeks imzalı bir Record her erişimi `| undefined` yapardı. */
type UniformName =
  | 'iTime'
  | 'iResolution'
  | 'hue'
  | 'hover'
  | 'rot'
  | 'hoverIntensity'
  | 'backgroundColor'
  | 'uColor1'
  | 'uColor2'
  | 'uColor3';

type Uniforms = { [K in UniformName]: { value: number | Float32Array } };

type Handle = {
  uniforms: Uniforms;
  start: () => void;
  stop: () => void;
  /** Donmuş kareyi basar: zaman STILL_TIME'a sabitlenir, hover sıfırlanır. */
  drawStill: () => void;
};

export function Orb({
  className,
  color1 = '#12e193',
  color2 = '#37e0d0',
  color3 = '#06292f',
  hue = 0,
  hoverIntensity = 0.14,
  rotateOnHover = true,
  forceHoverState = false,
}: OrbProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const handleRef = useRef<Handle | null>(null);
  const reduce = useReducedMotion();

  /* Kare döngüsünün okuduğu iki davranış bayrağı. Ref, çünkü döngü kurulum
     effect'inin içinde ve prop'ları kapanışta dondurur; ref'in kimliği sabit
     olduğu için değişen değeri bir sonraki karede görüyor. */
  const rotateRef = useRef(rotateOnHover);
  const forceRef = useRef(forceHoverState);
  rotateRef.current = rotateOnHover;
  forceRef.current = forceHoverState;

  /* --- Kurulum. Bir kez; prop değişimi ikinci effect'ten uniform'a yazılır. */
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const renderer = new Renderer({
      alpha: true,
      premultipliedAlpha: true,
      antialias: false,
      dpr: 1,
    });

    const gl = renderer.gl;
    gl.clearColor(0, 0, 0, 0);
    const canvas = gl.canvas as HTMLCanvasElement;
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.display = 'block';
    container.appendChild(canvas);

    /* İnce imleci olmayan cihazda hover tepkisi yok: DESIGN.md §2 (hover
       yalnızca hover destekleyen cihazda). Dokunmatikte "yaklaşma" diye bir
       durum yok, `forceHoverState` dışında hover hep 0 kalıyor. */
    const finePointer =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    const program = new Program(gl, {
      vertex,
      fragment,
      uniforms: {
        iTime: { value: 0 },
        iResolution: { value: new Float32Array([1, 1, 1]) },
        hue: { value: hue },
        hover: { value: 0 },
        rot: { value: 0 },
        hoverIntensity: { value: hoverIntensity },
        /* Zemin koyu: şaderin `bgLuminance` dalı karanlık moda giriyor ve
           küre kendi alfasıyla çiziliyor. Zemini CSS veriyor, bu değer
           yalnızca o dalı seçmek için. */
        backgroundColor: { value: new Float32Array([0, 0, 0]) },
        uColor1: { value: new Float32Array(hexToRgb(color1)) },
        uColor2: { value: new Float32Array(hexToRgb(color2)) },
        uColor3: { value: new Float32Array(hexToRgb(color3)) },
      },
    });

    const mesh = new Mesh(gl, { geometry: new Triangle(gl), program });
    const uniforms = program.uniforms as Uniforms;

    const draw = () => renderer.render({ scene: mesh });

    /* Tampon alanını tavana göre ölçekle — bkz. MAX_BUFFER_PIXELS.
       Gözlemci `resize` DEĞİL ResizeObserver: katmanın ölçüsü kabından
       geliyor (yükseklik yüzdesi + aspect-ratio), pencere hiç değişmeden de
       değişebiliyor. */
    const setSize = () => {
      /* Ölçü `offsetWidth`ten, `getBoundingClientRect`ten DEĞİL: kap bir
         giriş animasyonunun içinde olabiliyor ve rect transform'u da
         hesaba katıyor. Ölçek 0.94'teyken alınan bir kare tamponu kalıcı
         olarak %6 küçük bırakıyordu — ResizeObserver bunu düzeltemez, çünkü
         transform border-box'ı değiştirmiyor, yani ikinci bir bildirim hiç
         gelmiyor. `offsetWidth` düzenin ölçüsü, transform'dan bağımsız. */
      const w = Math.max(1, container.offsetWidth);
      const h = Math.max(1, container.offsetHeight);
      const raw = Math.min(window.devicePixelRatio || 1, 2);
      const area = w * h * raw * raw;
      renderer.dpr = area > MAX_BUFFER_PIXELS ? raw * Math.sqrt(MAX_BUFFER_PIXELS / area) : raw;
      renderer.setSize(w, h);
      const res = uniforms.iResolution.value as Float32Array;
      res[0] = gl.drawingBufferWidth;
      res[1] = gl.drawingBufferHeight;
      res[2] = gl.drawingBufferWidth / Math.max(1, gl.drawingBufferHeight);
      draw();
    };

    const ro = new ResizeObserver(setSize);
    ro.observe(container);
    setSize();

    /* Katman `pointer-events: none` — dinleyici canvas'a değil window'a bağlı,
       konum canvas'ın dikdörtgenine göre normalleniyor. Eşik kaynaktaki gibi
       0.8: küre uv biriminde 1.0 yarıçapında, yani imleç halkanın hemen
       içine girdiğinde tepki başlıyor. */
    let targetHover = 0;

    const onPointerMove = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      const size = Math.min(rect.width, rect.height);
      const uvX = ((e.clientX - rect.left - rect.width / 2) / size) * 2;
      const uvY = ((e.clientY - rect.top - rect.height / 2) / size) * 2;
      targetHover = Math.sqrt(uvX * uvX + uvY * uvY) < 0.8 ? 1 : 0;
    };
    const onPointerLeave = () => {
      targetHover = 0;
    };
    if (finePointer) {
      window.addEventListener('pointermove', onPointerMove, { passive: true });
      document.addEventListener('pointerleave', onPointerLeave);
    }

    let raf = 0;
    let inView = true;
    let pageVisible = !document.hidden;
    /* Azaltılmış hareket ilk karede biliniyor olmalı: useReducedMotion ilk
       render'da null döner, bir kare animasyon sızardı. */
    let animate = !(
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
    let currentRot = 0;
    let last = 0;
    const t0 = performance.now();
    const ROTATION_SPEED = 0.3;

    const loop = (t: number) => {
      const time = (t - t0) * 0.001;
      /* Döngü duraklayıp devam ettiğinde `dt` koca bir sıçrama olmasın diye
         son kare zamanı üstünden hesaplanıyor ve tavanlanıyor. */
      const dt = last === 0 ? 0 : Math.min(0.05, time - last);
      last = time;

      uniforms.iTime.value = time;

      const effectiveHover = forceRef.current ? 1 : targetHover;
      const hoverNow = uniforms.hover.value as number;
      uniforms.hover.value = hoverNow + (effectiveHover - hoverNow) * 0.1;

      if (rotateRef.current && effectiveHover > 0.5) currentRot += dt * ROTATION_SPEED;
      uniforms.rot.value = currentRot;

      draw();
      raf = requestAnimationFrame(loop);
    };

    const start = () => {
      if (animate && inView && pageVisible && raf === 0) {
        last = 0;
        raf = requestAnimationFrame(loop);
      }
    };
    const stop = () => {
      if (raf !== 0) {
        cancelAnimationFrame(raf);
        raf = 0;
      }
    };

    handleRef.current = {
      uniforms,
      start: () => {
        animate = true;
        start();
      },
      stop: () => {
        animate = false;
        stop();
      },
      drawStill: () => {
        uniforms.iTime.value = STILL_TIME;
        uniforms.hover.value = 0;
        uniforms.rot.value = 0;
        draw();
      },
    };

    const io = new IntersectionObserver(
      (entries) => {
        inView = entries.some((entry) => entry.isIntersecting);
        if (inView) start();
        else stop();
      },
      { threshold: 0 },
    );
    io.observe(container);

    const onVisibility = () => {
      pageVisible = !document.hidden;
      if (pageVisible) start();
      else stop();
    };
    document.addEventListener('visibilitychange', onVisibility);

    start();

    return () => {
      stop();
      handleRef.current = null;
      ro.disconnect();
      io.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerleave', onPointerLeave);
      canvas.remove();
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    };
    // Kurulum bir kez çalışır; prop'lar aşağıdaki effect'ten uniform'a yazılır.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* --- Azaltılmış hareket. Küre kaybolmuyor, duruyor. --------------------- */
  useEffect(() => {
    const handle = handleRef.current;
    if (!handle || reduce === null) return;
    if (reduce) {
      handle.stop();
      handle.drawStill();
    } else {
      handle.start();
    }
  }, [reduce]);

  /* --- Prop → uniform. Yeniden kurulum yok. ------------------------------- */
  useEffect(() => {
    const handle = handleRef.current;
    if (!handle) return;
    const u = handle.uniforms;
    u.hue.value = hue;
    u.hoverIntensity.value = hoverIntensity;
    (u.uColor1.value as Float32Array).set(hexToRgb(color1));
    (u.uColor2.value as Float32Array).set(hexToRgb(color2));
    (u.uColor3.value as Float32Array).set(hexToRgb(color3));
    if (reduce) handle.drawStill();
  }, [color1, color2, color3, hue, hoverIntensity, reduce]);

  return <div ref={containerRef} className={className} aria-hidden="true" />;
}
